import React from 'react';
import { Autocomplete, Chip, TextField } from '@mui/material';
import identity from 'lodash/identity';
import { FieldError } from 'react-hook-form';

import { HStack } from '@src/components/HStack';
import { Icon } from '@src/components/Icon';
import { Label } from '@src/components/Typography';
import { VStack } from '@src/components/VStack';
import { getTestId } from '@src/utils/testIds';
import { BASE_TEXT_FIELD_HEIGHT } from '../TextField/util';
import type { OptionType } from '../types';
import { DefaultMultiSelectFieldProps, MultiSelectFieldControllerProps } from './interface';

const InternalMultiSelectDropdown = (
  props: DefaultMultiSelectFieldProps & MultiSelectFieldControllerProps,
) => {
  const {
    disabled,
    error,
    formatOnChange = identity,
    helperText,
    label,
    onChange,
    options,
    parseValue = identity,
    parseError = parseValue,
    value: fieldValue,
    placeholder = 'Select',
  } = props;

  const getHelperText = (error: FieldError | undefined) => {
    const parsedError = error ? parseError(error) : undefined;
    return parsedError ? parsedError.message : helperText;
  };

  const value = parseValue(fieldValue);

  const values: OptionType[] = value.map((v: any) => options.find((o) => o.value === v));

  return (
    <VStack>
      <Label size="s">{label}</Label>
      <Autocomplete
        {...props}
        data-testid={getTestId(props)}
        disableCloseOnSelect={true}
        disabled={disabled}
        getOptionLabel={(option: { label: string }): string => option.label}
        multiple={true}
        onChange={(_e, val: OptionType[]) => {
          const mappedValues = val.map((v) => v.value);
          const newValue = formatOnChange(mappedValues, fieldValue);
          onChange(newValue);
        }}
        options={options}
        renderInput={(params: any) => {
          const inputProps = {
            ...(params?.inputProps ?? {}),
          };
          return (
            <TextField
              {...params}
              error={error}
              helperText={getHelperText(error)}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <Icon name="search" iconSize="16px" color="secondary" />
                    <React.Fragment>{params.InputProps.startAdornment}</React.Fragment>
                  </>
                ),
                sx: {
                  boxSizing: 'border-box',
                  fontSize: '14px',
                  minHeight: `${BASE_TEXT_FIELD_HEIGHT}px`,
                },
              }}
              inputProps={inputProps}
              label={undefined}
              placeholder={placeholder}
            />
          );
        }}
        renderOption={(rProps, option: OptionType, { selected }) => {
          if (option.disabled) {
            rProps['aria-disabled'] = true;
          }
          return (
            <li {...rProps}>
              <HStack width="100%" justifyContent="space-between">
                {option.label}
                {selected && (
                  <Icon
                    name="check"
                    iconSize="20px"
                    variant="custom"
                    color="state.teal.secondary"
                  />
                )}
              </HStack>
            </li>
          );
        }}
        renderTags={(tagValue, getTagProps) => {
          return tagValue.map((option, index) => {
            return (
              // key is provided by getTagProps
              // eslint-disable-next-line react/jsx-key
              <Chip
                label={option?.label}
                {...getTagProps({ index })}
                disabled={options.find((o) => o.label === option?.label)?.disabled}
              />
            );
          });
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            paddingY: 0.5,
          },
        }}
        value={values}
      />
    </VStack>
  );
};

export default InternalMultiSelectDropdown;
